import { useContext, useMemo } from 'react';
import * as Combobox from '@givebutter/primitives/combobox';
import {
    faA,
    faBookHeart,
    faComment,
    faGift,
    faHeartHalfStroke,
    faNoteSticky,
    faPaperPlaneTop,
    faPhone,
} from '@fortawesome/pro-regular-svg-icons';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterContext } from 'components/filter/filters-next/FilterContext';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import { transformMultiselectToReadable } from 'components/filter/filters-next/readable';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import { injectEquals, multiSelectToSingle } from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    DownstreamPreprocessTransform,
    FilterFormValue,
    FilterProps,
    FilterValue,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
    WithItems,
} from 'components/filter/types';
import * as Form from 'components/form';
import { ValidationTextMap } from 'components/form/validations';
import { identity } from 'lodash';

interface WithResourceFilterFormValues extends FilterFormValue<WithItems[]> {}

export const filterKey: ServerKey[] = [
    'has_auction_items',
    'has_soft_credits',
    'has_internal_note',
    'has_public_message',
    'has_dedication',
    'has_name',
    'has_primary_email',
    'has_primary_phone',
];

export const Icon = (props: FilterProps) => {
    const { strategyType } = props;
    switch (strategyType) {
        case 'has_auction_items':
            return faGift;
        case 'has_soft_credits':
            return faHeartHalfStroke;
        case 'has_internal_note':
            return faNoteSticky;
        case 'has_public_message':
            return faComment;
        case 'has_dedication':
            return faBookHeart;
        case 'has_name':
            return faA;
        case 'has_primary_email':
            return faPaperPlaneTop;
        case 'has_primary_phone':
            return faPhone;
        default:
            return null;
    }
};

export const Label: LabelFilterComponent = ({ strategyType }) => {
    switch (strategyType) {
        case 'has_auction_items':
            return 'Auction item';
        case 'has_soft_credits':
            return 'Soft credit';
        case 'has_internal_note':
            return 'Internal note';
        case 'has_public_message':
            return 'Public message';
        case 'has_dedication':
            return 'Dedication';
        case 'has_name':
            return 'Name';
        case 'has_primary_email':
            return 'Email';
        case 'has_primary_phone':
            return 'Phone';
        default:
            return '';
    }
};

export const Filter: ContentFilterComponent = () => {
    const { strategyType: strategyTypeRaw } = useContext(FilterContext);
    const strategyType = strategyTypeRaw as ServerKey;

    const { defaultValue, ...rest } = useFilter<WithItems[]>();
    const methods = Form.useForm<WithResourceFilterFormValues>({
        defaultValues: {
            value: defaultValue ? defaultValue.value : [],
        },
        mode: 'onChange',
    });

    const { control, path } = methods;

    const ctx = useMemo(
        () => ({
            defaultValue,
            ...rest,
        }),
        [defaultValue, rest],
    );

    const resourceLabel = transformServerKeyToOptionLabel(strategyType);

    return (
        <FilterFormContext.Provider value={ctx}>
            <Form.Provider {...methods}>
                <FilterForm>
                    <FilterContainer>
                        <Form.Container>
                            <Form.Label name={path('value')}>
                                <Label strategyType={strategyType} />
                            </Form.Label>
                            <Form.Combobox
                                control={control}
                                name={path('value')}
                                allowMultiple={false}
                                rules={{ required: ValidationTextMap.Required }}
                            >
                                <FilterComboboxItems>
                                    <Combobox.Item value="with">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>With {resourceLabel}</Combobox.ItemText>
                                    </Combobox.Item>
                                    <Combobox.Item value="without">
                                        <Combobox.CheckboxIndicator />
                                        <Combobox.ItemText>
                                            Without {resourceLabel}
                                        </Combobox.ItemText>
                                    </Combobox.Item>
                                </FilterComboboxItems>
                            </Form.Combobox>
                            <Form.Error name={path('value')} />
                        </Form.Container>
                        <FilterSubmit />
                    </FilterContainer>
                </FilterForm>
            </Form.Provider>
        </FilterFormContext.Provider>
    );
};

export const Readable: ReadableFilterComponent = () => {
    const currentFilter = useCurrentFilter<WithItems[]>();
    const { strategyType } = useContext(FilterContext);
    const value = currentFilter?.value ?? [];
    const label = transformServerKeyToOptionLabel(strategyType as ServerKey);

    return (
        <ReadableFilterValue>
            {transformMultiselectToReadable(value, {
                transform: v =>
                    ({
                        with: `With ${label}`,
                        without: `Without ${label}`,
                    })[v],
            })}
        </ReadableFilterValue>
    );
};

const transformServerKeyToOptionLabel = (serverKey: ServerKey) => {
    switch (serverKey) {
        case 'has_auction_items':
            return 'auction items';
        case 'has_soft_credits':
            return 'soft credits';
        case 'has_internal_note':
            return 'internal note';
        case 'has_public_message':
            return 'public message';
        case 'has_dedication':
            return 'dedication';
        case 'has_name':
            return 'name';
        case 'has_primary_email':
            return 'email';
        case 'has_primary_phone':
            return 'phone';
        default:
            return '';
    }
};

export const downstream: DownstreamFilterTransform<WithItems> = identity;

export const preprocessDownstream: DownstreamPreprocessTransform<
    WithItems[] | WithItems
> = filter => (Array.isArray(filter.value) ? filter.value : [filter.value]);

export const upstream: UpstreamFilterTransform<WithItems> = (filter: FilterValue) => {
    const singleValue = multiSelectToSingle(filter.value);

    if (filter.serverKey === 'has_soft_credits') {
        return injectEquals({ value: singleValue === 'with' });
    }

    return injectEquals({ value: singleValue });
};
